import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { cartLineId } from "../lib/utils";
import { getProductById } from "../data/products";

const LS = {
  cart: "9one_cart",
  wishlist: "9one_wishlist",
  recent: "9one_recent",
};

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

const StoreContext = createContext(null);

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => read(LS.cart, []));
  const [wishlist, setWishlist] = useState(() => read(LS.wishlist, []));
  const [recent, setRecent] = useState(() => read(LS.recent, []));

  // UI state (drawers / panels) — not persisted.
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => write(LS.cart, cart), [cart]);
  useEffect(() => write(LS.wishlist, wishlist), [wishlist]);
  useEffect(() => write(LS.recent, recent), [recent]);

  // ---- Cart ----
  const addToCart = useCallback((product, { size, color, quantity = 1 }) => {
    const lineId = cartLineId(product.id, size, color);
    setCart((prev) => {
      const existing = prev.find((l) => l.lineId === lineId);
      if (existing) {
        return prev.map((l) =>
          l.lineId === lineId ? { ...l, quantity: l.quantity + quantity } : l
        );
      }
      return [
        ...prev,
        {
          lineId,
          id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.images?.[0],
          size,
          color,
          quantity,
        },
      ];
    });
    setCartOpen(true);
  }, []);

  const updateQuantity = useCallback((lineId, quantity) => {
    setCart((prev) =>
      prev
        .map((l) => (l.lineId === lineId ? { ...l, quantity } : l))
        .filter((l) => l.quantity > 0)
    );
  }, []);

  const removeFromCart = useCallback((lineId) => {
    setCart((prev) => prev.filter((l) => l.lineId !== lineId));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = useMemo(
    () => cart.reduce((n, l) => n + l.quantity, 0),
    [cart]
  );
  const subtotal = useMemo(
    () => cart.reduce((n, l) => n + l.price * l.quantity, 0),
    [cart]
  );

  // ---- Wishlist ----
  const toggleWishlist = useCallback((productId) => {
    setWishlist((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  }, []);
  const removeFromWishlist = useCallback((productId) => {
    setWishlist((prev) => prev.filter((id) => id !== productId));
  }, []);
  const inWishlist = useCallback(
    (productId) => wishlist.includes(productId),
    [wishlist]
  );
  const wishlistProducts = useMemo(
    () => wishlist.map(getProductById).filter(Boolean),
    [wishlist]
  );

  // ---- Recently viewed ----
  const pushRecent = useCallback((productId) => {
    setRecent((prev) => {
      const next = [productId, ...prev.filter((id) => id !== productId)];
      return next.slice(0, 8);
    });
  }, []);
  const recentProducts = useMemo(
    () => recent.map(getProductById).filter(Boolean),
    [recent]
  );

  const value = {
    // cart
    cart,
    cartCount,
    subtotal,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    // wishlist
    wishlist,
    wishlistProducts,
    toggleWishlist,
    removeFromWishlist,
    inWishlist,
    // recently viewed
    recentProducts,
    pushRecent,
    // ui
    cartOpen,
    setCartOpen,
    searchOpen,
    setSearchOpen,
    menuOpen,
    setMenuOpen,
  };

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
};
